import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, Download, ExternalLink, Plus, RefreshCw, X } from 'lucide-react';
import { Badge, ErrorState, LoadingState, describeError } from '@/components/primitives';
import useResource from '@/pages/dashboard/useResource';
import axios from 'axios';
import { API, apiGet, authHeaders, copyText, downloadFile } from '@/pages/dashboard/api';
import { ago } from '@/pages/dashboard/format';

/*
 * Brand protection: domains that imitate the organization's own. Found by checking generated lookalikes
 * in DNS and by public certificate transparency logs; no suspicious site is ever opened. Each finding is
 * triaged here, and links help report it to the registrar or to browser safe-browsing lists.
 */

const STATUSES = [
  { id: 'open', label: 'Open' },
  { id: 'investigating', label: 'Investigating' },
  { id: 'takedown_requested', label: 'Takedown requested' },
  { id: 'resolved', label: 'Taken down' },
  { id: 'legitimate', label: 'Ours or legitimate' },
  { id: 'false_positive', label: 'Not a threat' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.id, s.label]));
const STATUS_TONE = { open: 'warning', investigating: 'info', takedown_requested: 'accent', resolved: 'success', legitimate: 'neutral', false_positive: 'neutral' };
const RISK_TONE = { high: 'danger', medium: 'warning', low: 'neutral' };
const TECHNIQUE = {
  homoglyph: 'Look-alike characters', idn_homoglyph: 'Look-alike letters (other alphabet)', transposition: 'Swapped letters', omission: 'Missing letter',
  repetition: 'Repeated letter', replacement: 'Neighbouring key', insertion: 'Extra letter', hyphenation: 'Added hyphen', keyword: 'Trust word added',
  contains_brand: 'Contains your name', tld_swap: 'Different ending', dot_omission: 'Dot removed', similar: 'Very similar',
};
const ACTIVE = new Set(['open', 'investigating', 'takedown_requested']);
const WATCH_SOURCE = { profile: 'Your profile', credentials: 'Credentials', added: 'Added here' };

/** Show a domain safely: a stray click or paste never resolves it. */
const defang = (d) => String(d).replace(/\./g, '[.]');

function Chips({ items, onRemove, label }) {
  if (!items.length) return null;
  return (
    <span className="br-chips" aria-label={label}>
      {items.map((x) => (
        <span key={x.key} className="br-chip">
          {x.text}{x.tag && <i>{x.tag}</i>}
          {onRemove && x.removable && <button type="button" onClick={() => onRemove(x)} aria-label={`Remove ${x.text}`}><X aria-hidden="true" /></button>}
        </span>
      ))}
    </span>
  );
}

function Watchlist({ watch, onSave, saving }) {
  const [domain, setDomain] = useState('');
  const [brand, setBrand] = useState('');
  // Clear the box only when the save worked, so a rejected entry can be corrected rather than retyped.
  const submit = (kind) => async (e) => {
    e.preventDefault();
    if (kind === 'domain' && domain.trim() && await onSave({ extra_domains: [...watch.extra_domains, domain.trim()] })) setDomain('');
    if (kind === 'brand' && brand.trim() && await onSave({ extra_brands: [...watch.extra_brands, brand.trim()] })) setBrand('');
  };
  const domains = watch.domains.map((d) => ({ key: d.domain, text: d.display || d.domain, value: d.domain, tag: WATCH_SOURCE[d.source], removable: d.source === 'added' }));
  const brands = watch.brands.map((b) => ({ key: b, text: b, removable: watch.extra_brands.some((x) => x.toLowerCase() === b.toLowerCase()) }));
  return (
    <section className="br-card" aria-label="What is protected">
      <div className="br-card-h"><h2 className="br-h2">What is protected</h2></div>
      <div className="br-watch">
        <div>
          <h3 className="br-h3">Your domains</h3>
          {domains.length ? <Chips items={domains} label="Protected domains" onRemove={(x) => onSave({ extra_domains: watch.extra_domains.filter((d) => d !== x.value) })} />
            : <p className="br-muted">None yet. Add the domains your customers see, such as your website and mail domain.</p>}
          <form className="br-inline" onSubmit={submit('domain')}>
            <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" aria-label="Add a domain" maxLength={253} data-testid="add-domain" />
            <button type="submit" className="is-btn is-btn--secondary is-btn--sm" disabled={saving || !domain.trim()}><Plus aria-hidden="true" /> Add domain</button>
          </form>
        </div>
        <div>
          <h3 className="br-h3">Brand names</h3>
          {brands.length ? <Chips items={brands} label="Protected brand names" onRemove={(x) => onSave({ extra_brands: watch.extra_brands.filter((b) => b.toLowerCase() !== x.text.toLowerCase()) })} />
            : <p className="br-muted">None yet.</p>}
          <form className="br-inline" onSubmit={submit('brand')}>
            <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Product or trading name" aria-label="Add a brand name" maxLength={60} />
            <button type="submit" className="is-btn is-btn--secondary is-btn--sm" disabled={saving || brand.trim().length < 3}><Plus aria-hidden="true" /> Add name</button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Detail({ f, onClose, onUpdate }) {
  const [note, setNote] = useState(f.note || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => setNote(f.note || ''), [f.id, f.note]);
  const save = async (patch, message) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/brand/findings/${f.id}`, patch, { headers: authHeaders() });
      onUpdate(res.data);
      toast.success(message);
    } catch (err) {
      toast.error("Couldn't update the finding", { description: describeError(err) });
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => { if (await copyText(f.domain)) toast.success('Copied the domain'); };
  const cert = f.cert?.first_seen;
  return (
    <aside className="br-panel" aria-label="Finding detail" data-testid="brand-panel">
      <div className="br-panel-h">
        <div className="min-w-0">
          <h2 className="br-panel-title" title={f.unicode || f.domain}>{defang(f.unicode || f.domain)}</h2>
          <p className="br-muted">Imitates {f.official || 'your brand'}</p>
        </div>
        <button type="button" className="br-x" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
      </div>
      <div className="br-panel-b">
        <div className="br-tags">
          <Badge size="sm" tone={RISK_TONE[f.risk]}>{f.risk} risk</Badge>
          <Badge size="sm" tone={STATUS_TONE[f.status]}>{STATUS_LABEL[f.status]}</Badge>
          <Badge size="sm">{TECHNIQUE[f.technique] || f.technique}</Badge>
        </div>
        <section>
          <h3 className="br-h3">Why it is flagged</h3>
          <ul className="br-reasons">{f.reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        </section>
        <dl className="br-facts">
          <div><dt>Addresses</dt><dd className="br-mono">{f.ips.length ? f.ips.map(defang).join(', ') : 'None'}</dd></div>
          <div><dt>Mail servers</dt><dd className="br-mono">{f.mx.length ? f.mx.map(defang).join(', ') : 'None'}</dd></div>
          <div><dt>Name servers</dt><dd className="br-mono">{f.ns.length ? f.ns.map(defang).join(', ') : 'None'}</dd></div>
          <div><dt>Certificate</dt><dd>{cert ? `First issued ${ago(cert)}${f.cert.issuer ? ` by ${f.cert.issuer}` : ''}` : 'None seen in public logs'}</dd></div>
          <div><dt>First seen here</dt><dd>{ago(f.first_seen_at)}</dd></div>
          <div><dt>Found by</dt><dd>{f.source.includes('certificate') ? 'Certificate logs' : 'Lookalike check'}</dd></div>
        </dl>
        <section>
          <h3 className="br-h3">Status</h3>
          <div className="br-status" role="group" aria-label="Status of this finding">
            {STATUSES.map((s) => (
              <button key={s.id} type="button" aria-pressed={f.status === s.id} disabled={busy}
                onClick={() => f.status !== s.id && save({ status: s.id }, `Marked ${s.label.toLowerCase()}`)}>{s.label}</button>
            ))}
          </div>
          <label className="br-note">
            <span className="br-muted">Note for your team</span>
            <textarea rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Registrar contacted, ticket reference, who owns it"
              onBlur={() => note !== (f.note || '') && save({ note }, 'Note saved')} />
          </label>
        </section>
        <section>
          <h3 className="br-h3">Take it down</h3>
          <p className="br-fine">These open third-party reporting pages. IntelliSecure never visits the suspicious site.</p>
          <div className="br-links">
            <a href={`https://lookup.icann.org/en/lookup?name=${encodeURIComponent(f.domain)}`} target="_blank" rel="noopener noreferrer">Find the registrar <ExternalLink aria-hidden="true" /></a>
            <a href={`https://safebrowsing.google.com/safebrowsing/report_phish/?url=${encodeURIComponent(`http://${f.domain}`)}`} target="_blank" rel="noopener noreferrer">Report to Google Safe Browsing <ExternalLink aria-hidden="true" /></a>
            <a href="https://www.microsoft.com/en-us/wdsi/support/report-unsafe-site" target="_blank" rel="noopener noreferrer">Report to Microsoft <ExternalLink aria-hidden="true" /></a>
            <button type="button" onClick={copy}><Copy aria-hidden="true" /> Copy the domain</button>
          </div>
        </section>
      </div>
    </aside>
  );
}

export default function Lookalikes() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') || 'active';
  const riskFilter = params.get('risk') || '';
  const selectedId = params.get('id');
  const overview = useResource(() => apiGet('/brand/overview'), [], { poll: false });
  const list = useResource(() => apiGet('/brand/findings'), [], { poll: false });
  const [saving, setSaving] = useState(false);
  const [overrides, setOverrides] = useState({});
  const o = overview.data;

  const set = useCallback((patch) => setParams((p) => {
    const next = new URLSearchParams(p);
    Object.entries(patch).forEach(([k, v]) => (!v ? next.delete(k) : next.set(k, v)));
    return next;
  }, { replace: true }), [setParams]);

  // While a scan runs, poll until it ends, then load what it found.
  useEffect(() => {
    if (!o?.running) return undefined;
    const t = setInterval(async () => { await overview.reload(); }, 3000);
    return () => clearInterval(t);
  }, [o?.running, overview.reload]);
  const wasRunning = React.useRef(false);
  useEffect(() => {
    if (wasRunning.current && o && !o.running) {
      list.reload();
      if (o.last_scan?.state === 'failed') toast.error('The scan failed', { description: o.last_scan.error });
      else toast.success(`Scan finished: ${o.last_scan?.found ?? 0} lookalike ${o.last_scan?.found === 1 ? 'domain' : 'domains'} found`);
    }
    wasRunning.current = Boolean(o?.running);
  }, [o, list.reload]);

  const findings = useMemo(() => (list.data?.findings || []).map((f) => overrides[f.id] || f), [list.data, overrides]);
  const shown = useMemo(() => findings.filter((f) => (view === 'all' ? true : view === 'active' ? ACTIVE.has(f.status) : f.status === view)
    && (!riskFilter || f.risk === riskFilter)), [findings, view, riskFilter]);
  const selected = findings.find((f) => f.id === selectedId) || null;
  const activeCount = findings.filter((f) => ACTIVE.has(f.status)).length;
  const high = findings.filter((f) => ACTIVE.has(f.status) && f.risk === 'high').length;
  const takedown = findings.filter((f) => f.status === 'takedown_requested').length;

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      await axios.put(`${API}/brand/config`, patch, { headers: authHeaders() });
      await overview.reload();
      toast.success('Watchlist updated', { description: 'The next scan will use it.' });
      return true;
    } catch (err) {
      toast.error("Couldn't update the watchlist", { description: describeError(err) });
      return false;
    } finally {
      setSaving(false);
    }
  };
  const scan = async () => {
    try {
      await axios.post(`${API}/brand/scan`, {}, { headers: authHeaders() });
      await overview.reload();
      toast.success('Scan started', { description: 'It usually takes under a minute.' });
    } catch (err) {
      toast.error("Couldn't start the scan", { description: describeError(err) });
    }
  };
  const exportCsv = async () => {
    try { await downloadFile('/brand/findings.csv', 'brand-protection-findings.csv'); toast.success('Exported'); } catch (err) { toast.error("Couldn't export", { description: describeError(err) }); }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && selectedId && !e.defaultPrevented) set({ id: null }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, set]);

  const ls = o?.last_scan;
  const nothingWatched = o && !o.watch.domains.length && !o.watch.brands.length;

  return (
    <div className="br-tab" data-testid="brand-lookalikes">
      <div className="br-toolbar">
        <p className="br-fine">Domains that imitate yours, found before your customers are phished. We check lookalike names in DNS and public certificate logs; no suspicious site is ever opened.</p>
        <div className="br-toolbar-actions">
          <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={exportCsv} disabled={!findings.length}><Download aria-hidden="true" /> Export CSV</button>
          <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={scan} disabled={o?.running || nothingWatched} data-testid="scan-now">
            <RefreshCw className={o?.running ? 'is-spinner' : undefined} aria-hidden="true" /> {o?.running ? 'Scanning…' : 'Scan now'}
          </button>
        </div>
      </div>

      {overview.loading && !o ? <LoadingState label="brand protection" rows={6} />
        : overview.error && !o ? <ErrorState title="Couldn't load brand protection" error={overview.error} onRetry={overview.reload} />
          : (
            <>
              <section className="is-stats" aria-label="Key numbers">
                <div className="is-stats-item"><span className="is-stats-l">Active lookalikes</span><b className="is-stats-v">{activeCount}</b><span className="is-stats-s">Open or being handled</span></div>
                <div className="is-stats-item"><span className="is-stats-l">High risk</span><b className="is-stats-v" data-tone={high ? 'critical' : undefined}>{high}</b><span className="is-stats-s">Live, can send mail, deceptive</span></div>
                <div className="is-stats-item"><span className="is-stats-l">Takedowns in progress</span><b className="is-stats-v">{takedown}</b><span className="is-stats-s">{o.counts.resolved} already taken down</span></div>
                <div className="is-stats-item"><span className="is-stats-l">Domains protected</span><b className="is-stats-v">{o.watch.domains.length}</b>
                  <span className="is-stats-s">{ls ? `Last scan ${ago(ls.started_at)}` : 'Not scanned yet'}</span></div>
              </section>

              {ls?.state === 'failed' && <p className="br-warn" role="alert">The last scan did not complete: {ls.error}</p>}
              {ls && ls.state !== 'failed' && ls.notes?.length > 0 && <p className="br-fine">{ls.notes.join('. ')}.</p>}

              <Watchlist watch={o.watch} onSave={saveConfig} saving={saving} />

              {nothingWatched ? (
                <div className="br-empty"><b>Add a domain to start</b><span>Once a domain is protected, we scan for lookalikes every day and notify you when a dangerous one goes live.</span></div>
              ) : list.loading && !list.data ? <LoadingState label="findings" rows={4} />
                : list.error && !list.data ? <ErrorState title="Couldn't load findings" error={list.error} onRetry={list.reload} />
                  : (
                    <div className={`br-triage${selected ? ' has-panel' : ''}`}>
                      <section className="br-card" aria-label="Findings">
                        <div className="br-card-h">
                          <h2 className="br-h2">Findings</h2>
                          <div className="br-filters">
                            <div className="is-seg" role="group" aria-label="Show">
                              {[['active', 'Active'], ['resolved', 'Taken down'], ['all', 'All']].map(([id, l]) => (
                                <button key={id} type="button" aria-pressed={view === id} onClick={() => set({ view: id === 'active' ? null : id })} data-testid={`view-${id}`}>{l}</button>
                              ))}
                            </div>
                            <div className="is-seg" role="group" aria-label="Risk">
                              {[['', 'Any risk'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']].map(([id, l]) => (
                                <button key={id || 'any'} type="button" aria-pressed={riskFilter === id} onClick={() => set({ risk: id })}>{l}</button>
                              ))}
                            </div>
                          </div>
                        </div>
                        {shown.length === 0 ? (
                          <div className="br-empty" data-testid="brand-empty">
                            <b>{findings.length ? 'Nothing in this view' : ls ? 'No lookalike domains found' : 'No scan has run yet'}</b>
                            <span>{findings.length ? 'Try another filter.' : ls ? 'No registered domain imitating yours turned up. We check again every day.' : 'Scan now to check generated lookalikes of your domains.'}</span>
                          </div>
                        ) : (
                          <div className="br-table-wrap">
                            <table className="br-table">
                              <thead><tr><th scope="col">Risk</th><th scope="col">Lookalike</th><th scope="col">How it imitates</th><th scope="col">Live</th><th scope="col">First seen</th><th scope="col">Status</th></tr></thead>
                              <tbody>
                                {shown.map((f) => (
                                  <tr key={f.id} className={selectedId === f.id ? 'is-on' : undefined} onClick={() => set({ id: f.id === selectedId ? null : f.id })} data-testid={`finding-${f.domain}`}>
                                    <td><Badge size="sm" tone={RISK_TONE[f.risk]}>{f.risk}</Badge></td>
                                    <td className="br-td-main">
                                      <button type="button" className="br-row-btn" onClick={(e) => { e.stopPropagation(); set({ id: f.id === selectedId ? null : f.id }); }}>{defang(f.unicode || f.domain)}</button>
                                      <span className="br-muted">imitates {f.official}</span>
                                    </td>
                                    <td>{TECHNIQUE[f.technique] || f.technique}</td>
                                    <td className="br-nowrap">{f.live === false ? <span className="br-muted">No longer resolves</span>
                                      : [f.ips.length ? 'Website' : null, f.mx.length ? 'Email' : null].filter(Boolean).join(' and ') || <span className="br-muted">Registered only</span>}</td>
                                    <td className="br-nowrap">{ago(f.first_seen_at)}</td>
                                    <td><Badge size="sm" tone={STATUS_TONE[f.status]}>{STATUS_LABEL[f.status]}</Badge></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </section>
                      {selected && <Detail f={selected} onClose={() => set({ id: null })} onUpdate={(u) => setOverrides((x) => ({ ...x, [u.id]: u }))} />}
                    </div>
                  )}
              <p className="br-fine">{o.note} Certificate data from public transparency logs.</p>
            </>
          )}
    </div>
  );
}
